import { useAsyncNew } from 'hooks/useAsyncNew';
import NetworkAppsService from 'services/network-apps/NetworkApps';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import useDidMountEffect from 'hooks/useDidMountEffect';
import { Box, Chip, Stack } from '@mui/material';
import ServerAutoComplete from 'components/shared/server-auto-complete';
import SimpleSelect from 'components/select/SimpleSelect';
import CloseIcon from '@mui/icons-material/Close';
import Search from 'components/shared/search';
import { useSelector } from 'react-redux';
import StoreService from 'services/store/Store';

const SelectedFilters = ({ filters, readOnly, clearFilter }) => {
  const allowed = [
    'name',
    'coverageCountries',
    'networkType',
    'coverageType',
    'networkTechType',
    'service',
    'credentialsType',
    'networkProviders',
    'sku'
  ];

  const list = Object.entries(filters)
    .filter(([filterName, val]) => allowed.includes(filterName) && val)
    .reduce((acc, [filterName, filterValues]) => {
      acc[filterName] = Array.isArray(filterValues) ? filterValues : [
        {
          label: filterValues,
          value: filterName
        }
      ];
      return acc;
    }, {});

  return <>
    {Object.entries(list).map(([filterName, filterValues]) => (
      <Fragment key={filterName}>
        {filterValues.map((filter) => (
          <Chip
            color="primary"
            disabled={readOnly[filterName]}
            key={filter.value}
            label={filter.label}
            onDelete={() => clearFilter(filterName, filter.value)}
            deleteIcon={<CloseIcon />}
          />
        ))}
      </Fragment>
    ))}
  </>
}

const CountriesList = ({ onSelect, selected }) => {

  const handleCountryMenuItemSelect = (values) => {
    onSelect(values);
  };

  const countries = useAsyncNew(NetworkAppsService.getCountriesList, { immediate: false });

  const handleOnCountrySearch = async (value) => {
    await countries.execute({ title: value, limit: 10, 'sort.title': 'ASC' });
  };

  return <ServerAutoComplete
    label={'Countries'}
    InputProps={{
      label: 'Countries',
      placeholder: null,
    }}
    size="medium"
    multiple={true}
    getOptionValue={(option) => option.isoCode}
    onSelect={(items) => handleCountryMenuItemSelect(items)}
    onOpen={() => countries.execute()}
    loading={countries.isLoading}
    onSearch={handleOnCountrySearch}
    data={countries?.data?.countries}
    transform={(item) => item}
    getOptionLabel={(option) => option?.name || ''}
    value={selected}
    renderOption={(props, option) => {
      return (
        <li {...props} key={option.isoCode}>
          {option.name}
        </li>
      );
    }} />
}

const SkuSelectList = ({ value, onChange }) => {

  const clientUuid = useSelector((state) => {
    const { id: clientId, uuid: clientUuid } = state.account.selected;
    return clientUuid;
  });

  const [isOpenedOnce, setOpenedOnce] = useState(false);

  const storeItems = useAsyncNew(StoreService.getStoreItems, { immediate: false });

  useEffect(() => {
    if (!isOpenedOnce && (storeItems.status === 'idle') && value.filter(val => val).length) {
      storeItems.execute({ clientUuid });
    }
  }, [value])

  const handleOpen = () => {
    if (isOpenedOnce || storeItems.isSuccess) return;
    setOpenedOnce(true);
    storeItems.execute({ clientUuid });
  }

  return <SimpleSelect
    multiple={true}
    loading={storeItems.isLoading}
    onMenuItemSelect={(items) => onChange('sku', items)}
    label={'SKU'}
    getOptionValue={(option) => option.id}
    getOptionLabel={(option) => option.sku}
    menuItems={storeItems?.data?.data ?? []}
    selectedFilters={value}
    onOpen={handleOpen}
  />
};

const PlanFilters = ({
  filters = {},
  onFilter,
  isModal = false,
  readOnly = {},
  selected = {},
  filtersData = {}
}) => {

  const predefinedData = filtersData;
  const selectedList = selected;

  const [operators, setOperators] = useState({});

  const clearFilter = (name, value) => {
    if (name === 'name') {
      onFilter(name, '');
      return;
    }
    if (name === 'coverageCountries') {
      setOperators(prev => {
        const { [value]: omit, ...rest } = prev;
        return { ...rest };
      });
    }
    const val = filters[name].filter(filter => filter.value !== value);
    onFilter(name, val);
  };


  const handleMenuItemSelect = (filterName, values) => {
    onFilter(filterName, values);
  };

  const handleCountryMenuItemSelect = (values) => {
    const operators = values.reduce((acc, item) => {
      acc[item.value] = item.raw.networkProviders;
      return acc;
    }, {});

    setOperators(operators);
    handleMenuItemSelect('coverageCountries', values);
  };

  const handleOnSearch = (value) => {
    handleMenuItemSelect('name', value);
  };

  const getOperators = () => {
    return Object.entries(operators).reduce((acc, item) => {
      const [key, value] = item;
      return [...acc, ...value];
    }, []);
  };

  return <Stack spacing={2} alignItems={'start'} sx={{ width: '100%', mb: 4 }}>

    <Stack spacing={1} direction={isModal ? 'column' : 'row'} sx={{ width: '100%', minHeight: '40px' }}>
      <Box>
        <Search
          value={selectedList['name'] ?? ''}
          onSearch={handleOnSearch}
          sx={{ width: '100%' }}
        />
      </Box>
      <Stack direction={'row'} spacing={1}>
        <Box sx={{ my: '0 !important', flex: 1, minWidth: '133px' }}>
          <CountriesList
            selected={selectedList['coverageCountries'] ?? []}
            onSelect={handleCountryMenuItemSelect}
          />
        </Box>

        {
          <Box sx={{ my: '0 !important', flex: 1, minWidth: '133px' }}>
            <SimpleSelect
              inputProps={{ readOnly: readOnly?.networkProviders ?? false }}
              disabled={Object.keys(operators).length === 0}
              multiple={true}
              readOnly={readOnly?.networkProviders ?? false}
              onMenuItemSelect={(items) => handleMenuItemSelect('networkProviders', items)}
              label={'Operators'}
              menuItems={getOperators()}
              selectedFilters={selectedList['networkProviders'] ?? []}

            />
          </Box>
        }
        <Box sx={{ my: '0 !important', flex: 1, minWidth: '133px' }}>
          <SimpleSelect
            inputProps={{ readOnly: readOnly?.networkTechType ?? false }}
            multiple={true}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            onMenuItemSelect={(items) => handleMenuItemSelect('networkTechType', items)}
            label={'Radio Tech'}
            menuItems={predefinedData.radioTechs}
            selectedFilters={selectedList['networkTechType'] ?? []} />
        </Box>
        <Box sx={{ my: '0 !important', flex: 1, minWidth: '133px' }}>
          <SimpleSelect
            inputProps={{ readOnly: readOnly?.coverageType ?? false }}
            multiple={true}
            onMenuItemSelect={(items) => handleMenuItemSelect('coverageType', items)}
            label={'Coverage'}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            menuItems={predefinedData.coverageTypes}
            selectedFilters={selectedList['coverageType'] ?? []} />
        </Box>
      </Stack>
      <Stack direction={'row'} spacing={1}>
        <Box sx={{ my: '0 !important', flex: 1, minWidth: '133px' }}>
          <SimpleSelect
            multiple={true}
            onMenuItemSelect={(items) => handleMenuItemSelect('service', items)}
            inputProps={{ readOnly: readOnly?.service ?? false }}
            label={'Services'}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            menuItems={predefinedData.services}
            selectedFilters={selectedList['service'] ?? []} />
        </Box>
        <Box sx={{ my: '0 !important', flex: 1, minWidth: '133px' }}>
          <SimpleSelect
            inputProps={{ readOnly: readOnly?.credentialsType ?? false }}
            multiple={true}
            onMenuItemSelect={(items) => handleMenuItemSelect('credentialsType', items)}
            label={'Credential'}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            menuItems={predefinedData.credentialTypes}
            selectedFilters={selectedList['credentialsType'] ?? []} />
        </Box>
        <Box sx={{ my: '0 !important', flex: 1, minWidth: '133px' }}>
          <SimpleSelect
            inputProps={{ readOnly: readOnly?.networkType ?? false }}
            multiple={true}
            onMenuItemSelect={(items) => handleMenuItemSelect('networkType', items)}
            label={'Networks'}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            menuItems={predefinedData.networkTypes}
            selectedFilters={selectedList['networkType'] ?? []} />
        </Box>
        <Box sx={{ my: '0 !important', flex: 1, minWidth: '133px' }}>
          <SkuSelectList
            onChange={handleMenuItemSelect}
            value={selectedList['sku'] ?? []}
          />
        </Box>

      </Stack>
    </Stack>
    <Stack spacing={1} direction="row">
      <SelectedFilters
        readOnly={readOnly}
        clearFilter={clearFilter}
        filters={filters}
      />
    </Stack>
  </Stack>;
};

export default PlanFilters;
